import type { Locale, ZodiacSign } from './types.js';
import { ZODIAC_SIGN_ORDER } from './types.js';

export interface ZodiacSignInfo {
  id: ZodiacSign;
  /** Astrological glyph, used as the placeholder art until real sign
   *  illustrations exist. */
  symbol: string;
  name: Record<Locale, string>;
  /** Inclusive calendar range. Capricorn wraps the year end (Dec 22 – Jan 19). */
  start: { month: number; day: number };
  end: { month: number; day: number };
}

/** Seed data for the twelve signs, in calendar order. */
export const ZODIAC_SIGNS: ZodiacSignInfo[] = [
  { id: 'aries', symbol: '♈', name: { en: 'Aries', es: 'Aries' }, start: { month: 3, day: 21 }, end: { month: 4, day: 19 } },
  { id: 'taurus', symbol: '♉', name: { en: 'Taurus', es: 'Tauro' }, start: { month: 4, day: 20 }, end: { month: 5, day: 20 } },
  { id: 'gemini', symbol: '♊', name: { en: 'Gemini', es: 'Géminis' }, start: { month: 5, day: 21 }, end: { month: 6, day: 20 } },
  { id: 'cancer', symbol: '♋', name: { en: 'Cancer', es: 'Cáncer' }, start: { month: 6, day: 21 }, end: { month: 7, day: 22 } },
  { id: 'leo', symbol: '♌', name: { en: 'Leo', es: 'Leo' }, start: { month: 7, day: 23 }, end: { month: 8, day: 22 } },
  { id: 'virgo', symbol: '♍', name: { en: 'Virgo', es: 'Virgo' }, start: { month: 8, day: 23 }, end: { month: 9, day: 22 } },
  { id: 'libra', symbol: '♎', name: { en: 'Libra', es: 'Libra' }, start: { month: 9, day: 23 }, end: { month: 10, day: 22 } },
  { id: 'scorpio', symbol: '♏', name: { en: 'Scorpio', es: 'Escorpio' }, start: { month: 10, day: 23 }, end: { month: 11, day: 21 } },
  { id: 'sagittarius', symbol: '♐', name: { en: 'Sagittarius', es: 'Sagitario' }, start: { month: 11, day: 22 }, end: { month: 12, day: 21 } },
  { id: 'capricorn', symbol: '♑', name: { en: 'Capricorn', es: 'Capricornio' }, start: { month: 12, day: 22 }, end: { month: 1, day: 19 } },
  { id: 'aquarius', symbol: '♒', name: { en: 'Aquarius', es: 'Acuario' }, start: { month: 1, day: 20 }, end: { month: 2, day: 18 } },
  { id: 'pisces', symbol: '♓', name: { en: 'Pisces', es: 'Piscis' }, start: { month: 2, day: 19 }, end: { month: 3, day: 20 } },
];

const BY_ID = new Map(ZODIAC_SIGNS.map((sign) => [sign.id, sign]));

export function zodiacSignInfo(sign: ZodiacSign): ZodiacSignInfo {
  const info = BY_ID.get(sign);
  if (!info) throw new Error(`Unrecognised zodiac sign: ${sign}`);
  return info;
}

export function isZodiacSign(value: string): value is ZodiacSign {
  return (ZODIAC_SIGN_ORDER as readonly string[]).includes(value);
}

const MONTH_ABBR: Record<Locale, readonly string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
};

function formatMonthDay(month: number, day: number, locale: Locale): string {
  const abbr = MONTH_ABBR[locale][month - 1];
  // English reads "Mar 21"; Spanish conventionally reads "21 mar".
  return locale === 'es' ? `${day} ${abbr}` : `${abbr} ${day}`;
}

/** e.g. "Mar 21 – Apr 19" / "21 mar – 20 abr", for the Settings dropdown. */
export function zodiacDateRangeLabel(sign: ZodiacSign, locale: Locale): string {
  const info = zodiacSignInfo(sign);
  return `${formatMonthDay(info.start.month, info.start.day, locale)} – ${formatMonthDay(info.end.month, info.end.day, locale)}`;
}
